import React from 'react';
import { navigate } from 'gatsby';
import axios from 'axios';
import ReactPaginate from 'react-paginate';
import { capitalize, isEmpty, startCase, upperFirst } from 'lodash';
import BoxedContainer from '../components/layouts/BoxedContainer';
import Icons from '../components/layouts/Icons';
import useSearchChange from '../hooks/useSearchChange';
import Link from '../components/layouts/Link';

export default function SearchResults() {
  const isBrowser = typeof window !== 'undefined';
  const queryParams = new URLSearchParams(isBrowser && window.location.search);
  const [results, setResults] = React.useState([]);
  const [currentItems, setCurrentItems] = React.useState(null);
  const [pageCount, setPageCount] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(0);
  const itemsPerPage = 10;
  const [term, setTerm] = React.useState(queryParams.toString().slice(0, -1) || '');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const allowedTypes = ['newDSWPage', 'dubaiSportsWorld'];
  const notFoundMsgTitle = 'Nothing here matches your search';
  const notFoundMsgList = [
    'Suggestions',
    'Make sure all words are spelled correctly',
    'Try different search terms',
    'Try more general search terms',
    'Try fewer search terms',
  ];

  const { searchParams } = useSearchChange();

  // Search page
  const doSearch = () => {
    if (!term) return;
    setLoading(true);
    setError('');
    axios({
      method: 'get', //  you can set what request you want to be
      url: `https://cdn.umbraco.io/content/search?term=${term}&page=1&pageSize=1000`,
      headers: {
        'umb-project-alias': process.env.GATSBY_UMBRACO_ENVIRONMENT,
        'Content-Type': 'application/json',
        'Accept-Language': 'en-US',
      },
    })
      .then((res) => {
        const allowedResults =
          res.data._embedded && res.data._embedded.content
            ? res.data._embedded.content.filter((result) =>
                allowedTypes.includes(result.contentTypeAlias),
              )
            : [];
        setResults([...allowedResults]);
        setCurrentItems([...allowedResults].slice(0, itemsPerPage));
        setPageCount(Math.ceil(allowedResults.length / itemsPerPage));
        setLoading(false);
        setError('');
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  };

  const handlePageClick = ({ selected: selectedPage }) => {
    setCurrentPage(selectedPage);
  };

  React.useEffect(() => {
    doSearch();
  }, [term]);

  React.useEffect(() => {
    if (searchParams) setTerm(searchParams);
  }, [searchParams]);

  React.useEffect(() => {
    const offset = currentPage * itemsPerPage;
    setCurrentItems([...results].slice(offset, offset + itemsPerPage));
  }, [currentPage]);

  const updateTerm = (e) => {
    e.preventDefault();
    setTerm(e.target.value);
    e.target.focus();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      navigate(`/search?${term}`);
      doSearch();
    }
  };

  const handleLinkText = (text) => {
    const regStr = '^(http|https)://';
    const regEx = new RegExp(regStr, 'i');
    if (regEx.test(text)) {
      return text.replace(regEx, '');
    }
    return text;
  };
  return (
    <section className="bg-bg-1 lg:pt-[250px] pt-[150px] lg:pb-[200px] pb-[70px]">
      <BoxedContainer className="regular">
        <div className="md:mb-10 mb-6 relative md:w-fit w-full">
          <input
            type="text"
            value={term}
            onChange={updateTerm}
            onKeyDown={handleKeyDown}
            className="w-full md:w-[500px] h-[44px] bg-bg-1 text-darkGrey text-base border
            border-greyCloud px-4 outline-none font-roboto-condensed-regular"
          />
          <Icons
            icon="home-search-icon"
            className="fill-darkGrey absolute right-[16px] top-[10px] w-[24px] h-[24px]"
          />
        </div>
        <div className="flex flex-col lg:gap-y-[30px] gap-y-[20px]">
          {loading ? (
            <div className="flex items-center justify-center">
              <div
                className="border-t-bg-6 ease-linear rounded-full border-8 border-t-8 h-48
                    w-48 g-4 animate-spinner"
              />
            </div>
          ) : (
            <>
              {error && <div>${error}</div>}
              {isEmpty(currentItems) || (
                <div className="flex flex-col gap-y-10">
                  <div className="flex flex-col lg:gap-y-[30px] gap-y-[15px]">
                    {currentItems.map((result, index) => (
                      <div
                        key={`search-${index}`}
                        className="flex flex-col gap-y-[5px] border-b border-b-red"
                      >
                        <h3
                          className="text-black font-roboto-condensed-bold lg:text-[18px]
                          text-[15px] leading-7"
                        >
                          <Link to={result._url}>{startCase(result.name.replace(/-/g, ' '))}</Link>
                        </h3>
                        <div
                          className="text-bg-10 lg:text-base text-[14px] leading-6
                          font-roboto-condensed-regular"
                        >
                          <p>{result.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <ReactPaginate
                    className="flex flex-row items-center lg:justify-end justify-center
                    gap-x-3 text-[18px] font-roboto-regular pagination-selected:text-red"
                    breakLabel="..."
                    nextLabel={<Icons icon="pagination-right-arrow" className="w-[20px]" />}
                    onPageChange={handlePageClick}
                    pageRangeDisplayed={5}
                    previousLabel={<Icons icon="pagination-left-arrow" className="w-[20px]" />}
                    pageCount={pageCount}
                    renderOnZeroPageCount={null}
                    forcePage={currentPage}
                  />
                </div>
              )}
            </>
          )}
          {!loading && currentItems.length === 0 && (
            <>
              <div className="not-found-label">{notFoundMsgTitle}</div>
              {notFoundMsgList.map((msg) => (
                <li className="not-found-list">{msg}</li>
              ))}
            </>
          )}
        </div>
      </BoxedContainer>
    </section>
  );
}
